import { ContactUnknownAvatar } from '@/assets/icons/contacts';
import SantaAvatar from '@/assets/images/santa.png';
import { getInitials } from '@/lib/conversation';
import { renderExternalDataIcons } from '@/lib/external-data-icons';
import { isApiUrl } from '@/lib/media';
import { isSantaContact } from '@/lib/santa';
import { cn } from '@/lib/utils';
import { type CSSProperties, memo, useEffect, useRef, useState } from 'react';
import { useSignedMediaUrl } from './components/message-item/use-signed-media-url';

const urlCache = new Map<string, string>();

type LegacyAvatarSize = '3xs' | '2xs' | 'xs' | 'sm' | 'm' | 'md' | 'lg' | 'xl' | '2xl' | 'default';

// Size-mapped radius for `shape="square"`. Mirrors the DS Avatar scale
// (sm/md/lg → rounded-sm/md/xl) and extends it to the larger legacy sizes
// so squared avatars stay visually consistent with the design system.
const squareRadius: Record<LegacyAvatarSize, string> = {
  '3xs': 'rounded-sm',
  '2xs': 'rounded-sm',
  xs: 'rounded-md',
  sm: 'rounded-md',
  default: 'rounded-md',
  m: 'rounded-xl',
  md: 'rounded-xl',
  '2xl': 'rounded-2xl',
  lg: 'rounded-3xl',
  xl: 'rounded-3xl',
};

/**
 * @deprecated Use the DS wrappers instead: `@/components/ui/contact-avatar`
 * for external contacts (`primary` color) and `@/components/ui/member-avatar`
 * for team members (`highlight` color). This legacy avatar is kept only for
 * the few call sites that still depend on its multi-size scale, the
 * `integration` decoration, and the `shape="square"` mode. Do not add new
 * imports — migrate the remaining call sites as part of follow-up work.
 */
export const ContactAvatar = memo(
  ({
    name,
    size = 'default',
    shape = 'circle',
    className,
    style,
    integration,
    imageUrl,
    onClick,
  }: {
    name?: string;
    size?: LegacyAvatarSize;
    shape?: 'circle' | 'square';
    className?: string;
    style?: CSSProperties;
    integration?: { [key: string]: Record<string, unknown> };
    imageUrl?: string;
    onClick?: () => void;
  }) => {
    const initials = name ? getInitials(name) : '';
    const isSanta = name ? isSantaContact(name) : false;
    const { getSignedUrl } = useSignedMediaUrl();

    const [displayUrl, setDisplayUrl] = useState<string | undefined>(() => {
      return imageUrl && urlCache.has(imageUrl) ? urlCache.get(imageUrl) : undefined;
    });

    const processedUrlRef = useRef<string | undefined>(undefined);

    useEffect(() => {
      if (!imageUrl) {
        setDisplayUrl(undefined);
        processedUrlRef.current = undefined;
        return;
      }

      if (processedUrlRef.current === imageUrl) {
        return;
      }

      if (urlCache.has(imageUrl)) {
        const cachedUrl = urlCache.get(imageUrl);
        setDisplayUrl(cachedUrl);
        processedUrlRef.current = imageUrl;
        return;
      }

      if (!isApiUrl(imageUrl)) {
        setDisplayUrl(imageUrl);
        urlCache.set(imageUrl, imageUrl);
        processedUrlRef.current = imageUrl;
        return;
      }

      let cancelled = false;
      getSignedUrl(imageUrl).then((resolvedUrl) => {
        if (cancelled || !resolvedUrl) {
          return;
        }

        const img = new Image();
        img.onload = () => {
          if (!cancelled) {
            urlCache.set(imageUrl, resolvedUrl);
            setDisplayUrl(resolvedUrl);
            processedUrlRef.current = imageUrl;
          }
        };
        img.onerror = () => {
          if (!cancelled) {
            urlCache.set(imageUrl, resolvedUrl);
            setDisplayUrl(resolvedUrl);
            processedUrlRef.current = imageUrl;
          }
        };
        img.src = resolvedUrl;
      });

      return () => {
        cancelled = true;
      };
    }, [imageUrl, getSignedUrl]);

    const effectiveImageUrl = displayUrl;
    const imageSrc = isSanta ? SantaAvatar : effectiveImageUrl;
    const showImage = Boolean(imageSrc);

    return (
      <div
        className={cn('relative', className)}
        style={style}
        onClick={onClick}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onClick();
                }
              }
            : undefined
        }
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        <div
          className={cn(
            'relative size-7 flex items-center justify-center overflow-hidden bg-primary-alpha-5',
            shape === 'square' ? squareRadius[size] : 'rounded-full',
            size === '3xs' && 'size-[14px]',
            size === '2xs' && 'size-4',
            size === 'xs' && 'size-5',
            size === 'sm' && 'size-6',
            size === 'm' && 'size-8',
            size === 'md' && 'size-11',
            size === '2xl' && 'size-12',
            size === 'lg' && 'size-16',
            size === 'xl' && 'size-20',
          )}
        >
          {showImage ? (
            <img src={imageSrc} alt={name || ''} className="size-full object-cover" />
          ) : initials ? (
            <span
              className={cn(
                'text-body-12-bold select-none',
                size === '3xs' && 'text-[8px]',
                size === '2xs' && 'text-[8px]',
                size === 'xs' && 'text-[8px]',
                size === 'sm' && 'text-body-12-bold',
                size === 'm' && 'text-body-14-bold',
                size === 'md' && 'text-body-18-bold',
                size === 'lg' && 'text-[26px] font-semibold',
                size === 'xl' && 'text-[32px] font-semibold',
                size === '2xl' && 'text-body-20-bold',
              )}
            >
              {initials}
            </span>
          ) : (
            <ContactUnknownAvatar
              className={cn(
                'size-8 fill-primary',
                size === 'lg' && 'size-16',
                size === 'md' && 'size-11',
                size === 'm' && 'size-8',
                size === 'xs' && 'size-5',
                size === '2xs' && 'size-4',
                size === '3xs' && 'size-[14px]',
                size === '2xl' && 'size-12',
                size === 'xl' && 'size-20',
              )}
            />
          )}
        </div>
        {integration && (
          <div
            className={cn(
              'absolute size-4 rounded-full border-1 bg-white border-primary-100/20 flex items-center justify-center -top-1 -right-1 p-0.5',
              size === 'lg' && 'size-5 -top-1 -right-0',
              size === 'xs' && 'size-3 -top-0.5 -right-0.5',
              size === '2xs' && 'size-2.5 -top-0.5 -right-0.5',
              size === '3xs' && 'size-2 -top-0.5 -right-0.5',
              size === '2xl' && 'size-5 -top-1 -right-0',
              size === 'xl' && 'size-5 -top-1 -right-0',
            )}
          >
            {renderExternalDataIcons(integration)?.[0] ?? null}
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.imageUrl === nextProps.imageUrl &&
      prevProps.name === nextProps.name &&
      prevProps.size === nextProps.size &&
      prevProps.shape === nextProps.shape &&
      prevProps.integration === nextProps.integration &&
      prevProps.className === nextProps.className &&
      prevProps.style === nextProps.style &&
      prevProps.onClick === nextProps.onClick
    );
  },
);

ContactAvatar.displayName = 'ContactAvatar';
